import { FC, useEffect } from 'react';
import { StatsModal } from './StatsModal';
import { ExpandableTextCell } from '../../common/ExpandableTextCell';
import { formatDate } from '../../../utils/dateTime';
import { getFollowUpTypeLabel } from '../../../api/nextFollowUp';
import {
  useFlywheelContactsGQL,
  useFlywheelFollowUpsGQL,
  type GQLFlywheelContact,
  type GQLFollowUpDecision,
} from '../../../hooks/graphql/contacts/useFlywheelFollowUpsGQL';
import type { FlywheelDrilldownFilter } from '../../dashboard/widgetDefinitions';

interface FlywheelFollowUpsModalProps {
  filter: Extract<FlywheelDrilldownFilter, { kind: 'followups' }>;
  isOpen: boolean;
  onClose: () => void;
  timezone?: string;
  onOpenContact: (contactId: string) => void;
}

interface FlywheelContactsModalProps {
  filter: Extract<FlywheelDrilldownFilter, { kind: 'contacts' }>;
  isOpen: boolean;
  onClose: () => void;
  onOpenContact: (contactId: string) => void;
}

function contactName(decision: GQLFollowUpDecision): string {
  const name = [decision.contactFirstName, decision.contactLastName].filter(Boolean).join(' ');
  return (
    name ||
    decision.contactEmail ||
    decision.contactPhone ||
    decision.contactId ||
    'Unknown contact'
  );
}

function flowNames(contact: GQLFlywheelContact): string {
  const names = new Set<string>();
  for (const flow of [...(contact.allFlowExecutions ?? []), ...(contact.flowExecutions ?? [])]) {
    const name = flow.flowName?.trim();
    if (name) names.add(name);
  }
  return Array.from(names).join(', ');
}

export const FlywheelFollowUpsModal: FC<FlywheelFollowUpsModalProps> = ({
  filter,
  isOpen,
  onClose,
  timezone,
  onOpenContact,
}) => {
  const {
    decisions,
    total,
    loading,
    currentPage,
    pageSize,
    goToPage,
    resetPage,
  } = useFlywheelFollowUpsGQL({
    enabled: isOpen,
    decisionType: filter.decisionType,
    strategySegment: filter.strategySegment,
  });

  useEffect(() => {
    if (isOpen) resetPage();
  }, [isOpen, filter.decisionType, filter.strategySegment, resetPage]);

  return (
    <StatsModal
      isOpen={isOpen}
      onClose={onClose}
      title={filter.title}
      timezone={timezone}
      currentPage={currentPage}
      pageSize={pageSize}
      totalCount={total}
      isLoading={loading}
      onNextPage={() => goToPage(currentPage + 1)}
      onPrevPage={() => goToPage(currentPage - 1)}
      onPageChange={goToPage}
      paginationLabel="follow-ups"
      onRowClick={(item) => {
        const contactId = item.contactId as string | null | undefined;
        if (contactId) onOpenContact(contactId);
      }}
      columns={[
        {
          key: 'contact',
          header: 'Name',
          width: '260px',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            if (!decision.contactId) {
              return <span className="text-muted-foreground">Unknown contact</span>;
            }
            return (
              <button
                className="text-primaryfortext font-medium underline text-left"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenContact(decision.contactId as string);
                }}
              >
                {contactName(decision)}
              </button>
            );
          },
        },
        {
          key: 'decisionType',
          header: 'Type',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            return decision.decisionType ? (
              <span className="whitespace-nowrap">
                {getFollowUpTypeLabel(decision.decisionType)}
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          },
        },
        {
          key: 'strategyId',
          header: 'Strategy',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            const strategy = decision.strategyId?.trim();
            return strategy ? (
              <span className="whitespace-nowrap">
                {strategy.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
              </span>
            ) : (
              <span className="text-muted-foreground italic">Unknown</span>
            );
          },
        },
        {
          key: 'scheduledFor',
          header: 'Scheduled',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            return decision.scheduledFor ? (
              <span className="whitespace-nowrap">
                {formatDate(decision.scheduledFor, timezone, 'MMM D, YYYY h:mm A z')}
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          },
        },
        {
          key: 'outcome',
          header: 'Outcome',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            return decision.outcome ? (
              <span className="whitespace-nowrap">
                {decision.outcome.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
              </span>
            ) : (
              <span className="text-muted-foreground">Pending</span>
            );
          },
        },
        {
          key: 'reasoning',
          header: 'Reasoning',
          cell: (item: Record<string, unknown>) => {
            const decision = item as unknown as GQLFollowUpDecision;
            const text = decision.reasoning?.trim() || decision.decisionReason?.trim();
            if (!text) return <span className="text-muted-foreground">-</span>;
            return <ExpandableTextCell text={text} className="leading-relaxed" />;
          },
        },
      ]}
      data={
        decisions.map((decision) => ({
          ...decision,
          id: decision.decisionId,
        })) as unknown as Record<string, unknown>[]
      }
    />
  );
};

export const FlywheelContactsModal: FC<FlywheelContactsModalProps> = ({
  filter,
  isOpen,
  onClose,
  onOpenContact,
}) => {
  const { contacts, total, loading, currentPage, pageSize, goToPage, resetPage } =
    useFlywheelContactsGQL(isOpen);

  useEffect(() => {
    if (isOpen) resetPage();
  }, [isOpen, resetPage]);

  return (
    <StatsModal
      isOpen={isOpen}
      onClose={onClose}
      title={filter.title}
      currentPage={currentPage}
      pageSize={pageSize}
      totalCount={total}
      isLoading={loading}
      onNextPage={() => goToPage(currentPage + 1)}
      onPrevPage={() => goToPage(currentPage - 1)}
      onPageChange={goToPage}
      paginationLabel="contacts"
      onRowClick={(item) => onOpenContact(item.contactId as string)}
      columns={[
        {
          key: 'name',
          header: 'Name',
          width: '200px',
          cell: (item: Record<string, unknown>) => {
            const contact = item as unknown as GQLFlywheelContact;
            const name =
              [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
              contact.email ||
              contact.phone ||
              'Unknown contact';
            return (
              <button
                className="text-primaryfortext font-medium underline text-left"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenContact(contact.contactId);
                }}
              >
                {name}
              </button>
            );
          },
        },
        { key: 'phone', header: 'Phone' },
        { key: 'email', header: 'Email' },
        {
          key: 'recentOutcome',
          header: 'Recent Outcome',
          cell: (item: Record<string, unknown>) => {
            const outcome = (item.recentOutcome as string | null | undefined)?.trim();
            return outcome ? (
              <span className="whitespace-nowrap">
                {outcome.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          },
        },
        {
          key: 'flows',
          header: 'Flows',
          cell: (item: Record<string, unknown>) => {
            const names = flowNames(item as unknown as GQLFlywheelContact);
            return names ? (
              <span className="block line-clamp-2">{names}</span>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          },
        },
      ]}
      data={contacts.map((contact) => ({ ...contact, id: contact.contactId }))}
    />
  );
};
